import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiResponse, ApiUriEnum, EMPLOYEE_ID_KEY, SECRET_KEY, TEMP_TOKEN_KEY, USER_KEY} from '@shared/model';
import {ApiService, HttpService, NavigationService} from '@shared/service';
import {map} from 'rxjs/operators';
import {
  AuthResponse,
  CredentialDto,
  OtpActivationPayload,
  OtpVerificationPayload,
  RefreshPayload,
  SigninPayload,
  SigninResponse,
  TokenDto
} from '@security/model';
import {TokenService} from '@security/service';
import {HttpClient} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends ApiService {
  private localStorage = window.localStorage;
  isAuthenticated = false;

  constructor(
    public tokenService: TokenService,
    public override http: HttpService,
    public override httpClient: HttpClient,
    public navigation: NavigationService
  ) {
    super(http, httpClient);
    this.isAuthenticated = this.localStorage.getItem(USER_KEY) != null;
  }

  signin(payload: SigninPayload): Observable<ApiResponse> {
    return this.http.post(`${this.baseUrl}${ApiUriEnum.SIGNIN}`, payload, false).pipe(
      map((response: ApiResponse) => {
        if (response.result) {
          const signinResponse: SigninResponse = response.data as SigninResponse;
          sessionStorage.setItem(USER_KEY, JSON.stringify(signinResponse.user));
          sessionStorage.setItem(TEMP_TOKEN_KEY, signinResponse.tempToken);
          if (signinResponse.requires2FASetup) {
            sessionStorage.setItem(SECRET_KEY, signinResponse.secretKey!)
            this.navigation.navigateTo2FaInitialization();
          } else {
            this.navigation.navigateTo2Fa();
          }
        }
        return response;
      })
    )
  }

  getCurrentUser(): CredentialDto | undefined {
    const serializedCredential = this.localStorage.getItem(USER_KEY);
    if (serializedCredential === null) {
      return undefined;
    }

    return JSON.parse(serializedCredential);
  }

  refreshToken(refresh: RefreshPayload): Observable<ApiResponse> {
    return this.http.post(`${this.baseUrl}${ApiUriEnum.REFRESH_TOKEN}`, refresh, false).pipe(
      map((response: ApiResponse) => {
        if (response.result) {
          const tokenResponse: TokenDto = response.data as TokenDto;
          this.tokenService.saveToken(tokenResponse.access_token);
          this.tokenService.saveRefreshToken(tokenResponse.refresh_token);
          this.isAuthenticated = true;
        }
        return response;
      })
    )
  }

  logout(): void {
    this.tokenService.signOut();
    this.localStorage.removeItem(USER_KEY);
    this.isAuthenticated = false;
    this.navigation.navigateToUnsecure();
  }

  activate2Fa(payload: OtpActivationPayload) {
    return this.http.post(`${this.baseUrl}${ApiUriEnum.ACTIVATE_2FA}`, payload, false).pipe(
      map((response: ApiResponse) => {
        if (response.result) {
          sessionStorage.clear();
          const authResponse: AuthResponse = response.data as AuthResponse;
          this.tokenService.saveToken(authResponse.token.access_token);
          this.tokenService.saveRefreshToken(authResponse.token.refresh_token);
          this.isAuthenticated = true;
          this.localStorage.setItem(USER_KEY, JSON.stringify(authResponse.user));
          this.localStorage.setItem(EMPLOYEE_ID_KEY, authResponse.employeeId);
          this.navigation.navigateToSecure();
        }
        return response;
      })
    );
  }

  verifyOtp(payload: OtpVerificationPayload) {
    return this.http.post(`${this.baseUrl}${ApiUriEnum.VERIFY_OTP}`, payload, false).pipe(
      map((response: ApiResponse) => {
        if (response.result) {
          sessionStorage.clear();
          const signinResponse: AuthResponse = response.data as AuthResponse;
          this.tokenService.saveToken(signinResponse.token.access_token);
          this.tokenService.saveRefreshToken(signinResponse.token.refresh_token);
          this.isAuthenticated = true;
          this.localStorage.setItem(USER_KEY, JSON.stringify(signinResponse.user));
          this.localStorage.setItem(EMPLOYEE_ID_KEY, signinResponse.employeeId);
          this.navigation.navigateToSecure();
        }
        return response;
      })
    );
  }
}
